import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';

/**
 * Label selalu eksplisit di atas input — bukan placeholder saja. Placeholder
 * yang hilang saat diketik menyulitkan pengguna mengingat isi field, dan tidak
 * terbaca screen reader sebagai label.
 */

const CONTROL = cn(
  'w-full rounded-[var(--radius-input)] border border-border bg-surface',
  'px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-muted',
  'min-h-11 sm:min-h-10',
  'transition-[border-color,box-shadow] duration-150',
  'focus:border-primary disabled:bg-surface-alt disabled:text-ink-muted',
);

interface WrapProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /**
   * Menyembunyikan label secara VISUAL saja — dipakai di dalam tabel yang
   * judul kolomnya sudah menjelaskan isinya. Labelnya tetap ada di DOM supaya
   * pembaca layar tidak menemukan kotak isian tanpa nama.
   */
  hideLabel?: boolean;
  children: ReactNode;
}

function Wrap({ label, htmlFor, hint, error, required, hideLabel, children }: WrapProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={htmlFor}
        className={cn('text-sm font-medium text-ink', hideLabel && 'sr-only')}
      >
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-[13px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[13px] text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  hideLabel?: boolean;
}

export function Input({ label, hint, error, hideLabel, className, id, ...rest }: InputProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Wrap label={label} htmlFor={fieldId} hint={hint} error={error} required={rest.required} hideLabel={hideLabel}>
      <input
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, error && 'border-danger', className)}
      />
    </Wrap>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string;
  hideLabel?: boolean;
}

export function Select({ label, hint, error, hideLabel, className, id, children, ...rest }: SelectProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Wrap label={label} htmlFor={fieldId} hint={hint} error={error} required={rest.required} hideLabel={hideLabel}>
      <select
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, 'pr-9', error && 'border-danger', className)}
      >
        {children}
      </select>
    </Wrap>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
  hideLabel?: boolean;
}

export function Textarea({ label, hint, error, hideLabel, className, id, ...rest }: TextareaProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Wrap label={label} htmlFor={fieldId} hint={hint} error={error} required={rest.required} hideLabel={hideLabel}>
      <textarea
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, 'min-h-24 resize-y py-3', error && 'border-danger', className)}
      />
    </Wrap>
  );
}
